import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMemo, useState } from "react";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, addDays } from "date-fns";
import {
  cancelAppointment,
  listMyAppointmentsPatient,
  listSlots,
  normalizeAppointmentList,
  PATIENT_APPTS_QUERY_KEY,
  rescheduleAppointment,
} from "@/api/appointmentApi";
import {
  browserTimeZone,
  clinicTimeZoneOf,
  clinicZoneShortLabel,
  formatInstantInBrowserZone,
  formatInstantInClinicZone,
} from "@/lib/clinicTime";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentPatientContextCard } from "@/components/appointment/AppointmentPatientContextCard";
import { AppointmentStatusBadge } from "@/components/appointment/AppointmentStatusBadge";
import PrescriptionViewCard from "@/components/prescription/PrescriptionViewCard";
import {
  canCancelAppointment,
  canJoinCall,
  canPayAppointment,
} from "@/lib/appointmentLifecycle";
import type { Slot } from "@/types/domain";

export default function PatientAppointmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: rawAppts, isLoading } = useQuery({
    queryKey: PATIENT_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsPatient,
  });
  const appts = useMemo(
    () => normalizeAppointmentList(rawAppts),
    [rawAppts],
  );
  const appointment = useMemo(
    () => (id ? appts.find((a) => a.id === id) : undefined),
    [appts, id],
  );

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(() =>
    format(addDays(new Date(), 2), "yyyy-MM-dd"),
  );
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  const cancelMut = useMutation({
    mutationFn: (apptId: string) => cancelAppointment(apptId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY }),
  });

  const slotsQuery = useQuery({
    queryKey: ["reschedule-slots", appointment?.doctorId, rescheduleDate],
    queryFn: () => listSlots(appointment!.doctorId, rescheduleDate),
    enabled: rescheduleOpen && !!appointment?.doctorId && !!rescheduleDate,
  });

  const rescheduleMut = useMutation({
    mutationFn: () => {
      if (!appointment || !selectedSlot) throw new Error("Missing data");
      return rescheduleAppointment(appointment.id, selectedSlot.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY });
      setRescheduleOpen(false);
      setSelectedSlot(null);
      setRescheduleError(null);
    },
    onError: (e: unknown) => {
      const msg =
        e instanceof Error ? e.message : "Failed to reschedule appointment.";
      setRescheduleError(msg);
    },
  });

  const canReschedule = !!appointment && canCancelAppointment(appointment);

  if (!id) {
    return <Navigate to="/patient/appointments" replace />;
  }

  return (
    <Box>
      <PageHeader
        title="Appointment details"
        subtitle="Review your booking and take the next step."
        actions={
          <Button
            component={RouterLink}
            to="/patient/appointments"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            All appointments
          </Button>
        }
      />

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !appointment && (
        <Alert severity="warning">
          We could not find this appointment. It may have been removed or the
          link is invalid.
          <Box sx={{ mt: 2 }}>
            <Button component={RouterLink} to="/patient/appointments" variant="contained">
              Go to my appointments
            </Button>
          </Box>
        </Alert>
      )}

      {!isLoading && appointment && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography fontWeight={700} variant="h6">
                  {appointment.doctorName ?? "Doctor"}
                  {appointment.doctorSpecialization && (
                    <Typography
                      component="span"
                      color="text.secondary"
                      fontWeight={500}
                      sx={{ ml: 0.5 }}
                    >
                      · {appointment.doctorSpecialization}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatInstantInBrowserZone(
                    new Date(appointment.slotStart),
                    "EEE, d MMM yyyy · h:mm a",
                  )}
                  {" — "}
                  {formatInstantInBrowserZone(
                    new Date(appointment.slotEnd),
                    "h:mm a",
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Shown in your zone ({browserTimeZone()}) · clinic time:{" "}
                  {formatInstantInClinicZone(
                    new Date(appointment.slotStart),
                    "EEE, d MMM yyyy · h:mm a",
                    appointment.timeZone,
                  )}
                  {" — "}
                  {formatInstantInClinicZone(
                    new Date(appointment.slotEnd),
                    "h:mm a",
                    appointment.timeZone,
                  )}{" "}
                  {clinicZoneShortLabel(appointment.timeZone)} (
                  {clinicTimeZoneOf(appointment.timeZone)})
                </Typography>
                {appointment.consultationFee != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fee: ₹{appointment.consultationFee}
                  </Typography>
                )}
              </Box>
              <AppointmentStatusBadge status={appointment.status} />
            </Stack>

            <AppointmentPatientContextCard appointment={appointment} />

            {appointment.doctorRemark && (
              <Box sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Doctor's Remark
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {appointment.doctorRemark}
                </Typography>
              </Box>
            )}

            {appointment.status === "COMPLETED" && (
              <PrescriptionViewCard appointmentId={appointment.id} />
            )}

            {appointment.rescheduledToId && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This appointment was rescheduled.{" "}
                <RouterLink to={`/patient/appointments/${appointment.rescheduledToId}`}>
                  View new appointment
                </RouterLink>
              </Alert>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              {canPayAppointment(appointment) && (
                <Button
                  component={RouterLink}
                  to={`/patient/appointments/${appointment.id}/pay`}
                  variant="contained"
                  size="small"
                >
                  Pay now
                </Button>
              )}
              {canReschedule && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setRescheduleOpen(true)}
                >
                  Reschedule
                </Button>
              )}
              {canCancelAppointment(appointment) && (
                <Button
                  onClick={() => cancelMut.mutate(appointment.id)}
                  disabled={cancelMut.isPending}
                  variant="outlined"
                  color="error"
                  size="small"
                >
                  Cancel
                </Button>
              )}
              {canJoinCall(appointment) && (
                <Button
                  component={RouterLink}
                  to={`/patient/consult/${appointment.id}`}
                  variant="contained"
                  size="small"
                >
                  Join
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      <Dialog
        open={rescheduleOpen}
        onClose={() => { setRescheduleOpen(false); setRescheduleError(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick a new available slot with the same doctor. Rescheduling is not allowed if the
            appointment is less than 24 hours away.
          </Typography>
          {rescheduleError && (
            <Alert severity="error" sx={{ mb: 2 }}>{rescheduleError}</Alert>
          )}
          <TextField
            label="Date"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={rescheduleDate}
            onChange={(e) => {
              setRescheduleDate(e.target.value);
              setSelectedSlot(null);
            }}
            sx={{ mb: 2 }}
          />
          {slotsQuery.isLoading && (
            <Box sx={{ display: "grid", placeItems: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {slotsQuery.data && slotsQuery.data.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              No available slots on this date.
            </Typography>
          )}
          {slotsQuery.data && slotsQuery.data.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {slotsQuery.data.filter(s => s.available).map((slot) => (
                <Button
                  key={slot.id}
                  variant={selectedSlot?.id === slot.id ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setSelectedSlot(slot)}
                >
                  {slot.startTime.slice(0, 5)} – {slot.endTime.slice(0, 5)}
                </Button>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRescheduleOpen(false); setRescheduleError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedSlot || rescheduleMut.isPending}
            onClick={() => rescheduleMut.mutate()}
          >
            {rescheduleMut.isPending ? "Rescheduling…" : "Confirm Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
